'use client'

import { observer } from 'mobx-react-lite'
import type { NextPage } from 'next'
import styled from 'styled-components'
import SectionContainer from '@/components/v1/ui/sectionContainer'
import { BreadCrumbs } from '@/components/v1/ui/breadcrumbs'
import Head from 'next/head'

const H4Wrapper = styled.h4`
  font-size: 26px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  text-transform: uppercase;
  margin: 25px 0;
`

const ParagWrapper = styled.p`
  font-size: 18px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  margin: 25px 0;
  a {
    text-decoration: underline;
  }
`
const SpanWrapper = styled.span`
  font-size: 18px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: bold;
  line-height: 26px;
  width: 100%;
  display: flex;
  margin: 50px 0 25px 0;
`

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    border: 1px solid #dddddd;
    padding: 8px;
    text-align: left;
  }

  td {
    border: 1px solid #dddddd;
    padding: 8px;
    text-align: left;
  }
`

const PrivacyPage: NextPage = () => {
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: 'Privacy', href: 'privacy' },
  ]

  return (
    <>
      <Head>
        <title>
          Privacy Policy | Rock My Wedding: UK No.1 Wedding Resource
        </title>
        <meta
          name="description"
          content="Rock My Wedding Ltd is committed to protecting and respecting your privacy. Learn more today about how Rock My Wedding uses your data."
        />
      </Head>
      <SectionContainer>
        <BreadCrumbs crumbs={crumbs} />
      </SectionContainer>
      <SectionContainer>
        <H4Wrapper>privacy policy</H4Wrapper>
        <ParagWrapper>
          Rock My Wedding (“we”, “us” or “our”) is committed to protecting and
          respecting your privacy. This policy sets out the basis on which any
          personal data we collect from you, or that you provide to us, when you
          visit our website will be processed by us.
          <br />
          <br />
          It is important that you read this privacy policy together with any
          other privacy notice or fair processing notice we may provide on
          specific occasions when we are collecting or processing personal data
          about you so that you are fully aware of how and why we are using your
          data. This privacy policy supplements the other policies and notices
          and is not intended to override them.
          <br />
          <br />
          For the purposes of the Data Protection Act 2018/UK GDPR and, when it
          comes into force, the General Data Protection Regulation ((EU)
          2016/679) (“GDPR”) and all successor legislation to the Data
          Protection Act 2018/UK GDPR and the GDPR (the “Data Protection
          Legislation”), the data controller is Rock My Wedding (company no.
          7281888) of Rock My Wedding, The Metro Building, 1 Butterwick, London,
          W6 8DL.
          <br />
          <SpanWrapper>1. Information we collect from you</SpanWrapper>
          Personal data, or personal information, means any information about an
          individual from which that person can be identified. It does not
          include data where the identity has been removed (known as “anonymous
          data”).
          <br />
          <br />
          We may collect, use, store and transfer the following data about you:
          <br />
          <br />
          <ul>
            <li>
              <strong>Information you give us</strong>.This is information about
              you that you give us by filling in forms on our website or by
              corresponding with us by phone, e-mail or otherwise. It includes
              information you provide when you order our services, subscribe to
              our services or publications, request marketing to be sent to you,
              enter a competition, promotion or survey, when you report a
              problem with our site, and when you give us feedback. The
              information you give us may include your name, address, e-mail
              address and bank details.
            </li>
            <li>
              <strong>Information we collect about you</strong>. With regard to
              each of your visits to our site we may automatically collect the
              following information:
              <ul>
                <li>
                  technical information, including the Internet protocol (IP)
                  address used to connect your computer to the Internet, your
                  login information, browser type and version, time zone
                  setting, browser plug-in types and versions, operating system
                  and platform;
                </li>
                <li>
                  information about your visit, including the full Uniform
                  Resource Locators (URL), clickstream to, through and from our
                  site (including date and time), products you viewed or
                  searched for page response times, download errors, length of
                  visits to certain pages, page interaction information (such as
                  scrolling, clicks, and mouse-overs) and methods used to browse
                  away from the page.
                </li>
              </ul>
            </li>
          </ul>
          <br />
          <br />
          Information we receive from other sources. We may receive personal
          data about you from various third parties including, for example,
          business partners, sub-contractors in technical, payment and delivery
          services, advertising networks, analytics providers and search
          information providers.
          <br />
          <br />
          We may also collect, use and share aggregated data such as statistical
          or demographic data for any purpose. Aggregated data may be derived
          from your personal data but is not considered personal data in law as
          this data does not directly or indirectly reveal your identity.
          However, if we combine or connect aggregated data with your personal
          data so that it can directly or indirectly identify you, we treat the
          combined data as personal data which will be used in accordance with
          this privacy policy.
          <br />
          <br />
          We do not collect any special categories of personal data about you
          (this includes details about your race or ethnicity, religious or
          philosophical beliefs, sex life, sexual orientation, political
          opinions, trade union membership, information about your health and
          genetic and biometric data). Nor do we collect any information about
          criminal convictions and offences.
          <br />
          <br />
          Where we need to collect personal data by law, or under the terms of a
          contract we have with you and you fail to provide that data when
          requested, we may not be able to perform the contract we have or are
          trying to enter into with you (for example, to provide you with
          services). In this case, we may have to cancel a product or service
          you have with us but we will notify you if this is the case at the
          time.
          <SpanWrapper>2. Uses made of your information</SpanWrapper>
          We will only use your personal data when the law allows us to. Most
          commonly, we will use your personal information:
          <br />
          <br />
          <ul>
            <li>
              to carry out our obligations arising from any contracts entered
              into between you and us or to take steps at your request before
              entering into any contracts;
            </li>
            <li>
              to comply with any legal obligations to which we are subject; or
            </li>
            <li>
              where it is necessary for our or a third party’s legitimate
              interests, including for the purposes of preventing fraud, except
              where such interests are overridden by your interests or
              fundamental rights and freedoms. Where possible, we will try to
              provide you with an opt-out in respect of the use of your personal
              data for such purposes.
            </li>
          </ul>
          <br />
          <br />
          Generally we do not rely on consent as a legal basis for processing
          your personal information other than in relation to sending certain
          direct marketing communications to you via email.
          <SpanWrapper>
            3. Purposes for which we will use your personal data
          </SpanWrapper>
          We have set out below, in a table format, a description of all the
          ways we plan to use your personal data, and which of the legal bases
          we rely on to do so. We have also identified what our legitimate
          interests are where appropriate.
          <br />
          <br />
          Note that we may process your personal data for more than one lawful
          ground depending on the specific purpose for which we are using your
          data. Please contact us if you need details about the specific legal
          ground we are relying on to process your personal data where more than
          one ground has been set out in the table below.
          <br />
          <br />
          <StyledTable>
            <tr>
              <th>Purpose/Activity</th>
              <th>Type of data</th>
              <th>
                Lawful basis for processing including basis of legitimate
                interest
              </th>
            </tr>
            <tr>
              <td>
                To register you as a new customer and to enable you to set up an
                online account.
              </td>
              <td>Identity, contact details, technical data</td>
              <td>Performance of a contract with you</td>
            </tr>
            <tr>
              <td>
                To manage our relationship with you which will include:
                <ol type="a">
                  <li>
                    Notifying you about changes to our terms or privacy policy;
                  </li>
                  <li>Asking you to leave a review or take a survey.</li>
                </ol>
              </td>
              <td>Identity, contact details and account preferences</td>
              <td>
                <ol type="a">
                  <li>Performance of a contract with you</li>
                  <li>Necessary to comply with a legal obligation</li>
                  <li>
                    Necessary for our legitimate interests (to keep our records
                    updated and to study how customers use our
                    products/services)
                  </li>
                </ol>
              </td>
            </tr>
            <tr>
              <td>
                To administer and protect our business and this website
                (including troubleshooting, data analysis, testing, system
                maintenance, support, reporting and hosting of data)
              </td>
              <td>
                Identity, contact details and technical data such as IP address
              </td>
              <td>
                <ol type="a">
                  <li>
                    Necessary for our legitimate interests (for running our
                    business, provision of administration and IT services,
                    network security, to prevent fraud and in the context of a
                    business reorganisation or group restructuring exercise)
                  </li>
                  <li>Necessary to comply with a legal obligation</li>
                </ol>
              </td>
            </tr>
            <tr>
              <td>
                To use data analytics to improve our website, products/services,
                marketing, customer relationships and experiences
              </td>
              <td>Technical data such as IP address</td>
              <td>
                Necessary for our legitimate interests (to define types of
                customers for our products and services, to keep our website
                updated and relevant, to develop our business and to inform our
                marketing strategy)
              </td>
            </tr>
            <tr>
              <td>
                To make suggestions and recommendations to you about goods or
                services that may be of interest to you
              </td>
              <td>
                Identity, contact details, usage data and account preferences
              </td>
              <td>
                Necessary for our legitimate interests (to develop our
                products/services and grow our business)
              </td>
            </tr>
          </StyledTable>
          <SpanWrapper>Marketing</SpanWrapper>
          We strive to provide you with choices regarding certain personal data
          uses, particularly around marketing and advertising.
          <br />
          <br />
          We may use your identity, contact details, usage data, technical data
          and account preferences to form a view on what we think you may want
          or need, or what may be of interest to you. This is how we decide
          which products, services and offers may be relevant for you.
          <br />
          <br />
          You will receive marketing communications from us if you have
          requested information from us or if you have purchased or signed up
          for services from us and, in each case, you have not opted out of
          receiving that marketing.
          <br />
          <br />
          You can ask us or third parties to stop sending you marketing messages
          at any time by clicking the “unsubscribe” link at the bottom of every
          marketing email or by contacting us at any time via email at&nbsp;
          <a href="mailto:hello@rockmywedding.co.uk">
            hello@rockmywedding.co.uk
          </a>
          .
          <br />
          <br />
          Where you opt out of receiving these marketing messages, this will not
          apply to personal data provided to us as a result of a product/service
          purchase, warranty registration, product/service experience or other
          transactions.
          <SpanWrapper>Change of purpose</SpanWrapper>
          We will only use your personal data for the purposes for which we
          collected it, unless we reasonably consider that we need to use it for
          another reason and that reason is compatible with the original
          purpose.
          <br />
          <br />
          If you wish to get an explanation as to how the processing for the new
          purpose is compatible with the original purpose, please contact us.
          <br />
          <br />
          If we need to use your personal data for an unrelated purpose, we will
          notify you and we will explain the legal basis which allows us to do
          so.
          <br />
          <br />
          Please note that we may process your personal data without your
          knowledge or consent, in compliance with the above rules, where this
          is required or permitted by law.
          <SpanWrapper>4. Disclosure of your information</SpanWrapper>
          We may have to share your personal information with the third parties
          set out below, or any other third parties notified to you, for the
          purposes set out in paragraph 3 above:
          <br />
          <br />
          <ul>
            <li>
              Twitter, Facebook, Instagram, Pinterest, Google, and Google
              Analytics all of which are based in the US.
            </li>
            <li>
              Our marketing automation platform, MailChimp, which is based in
              the US.
            </li>
            <li>
              Our marketing platform, Mailerlite, which is based in the EU.
            </li>
            <li>
              Traffic optimisation and distribution services, Amazon Cloudfront
              and Cloudflare, which are based in the US.
            </li>
            <li>
              Service providers acting as processors who provide IT, payment,
              and system administration services, including Azure, Slack,
              Stripe, AHA and Microsoft.
            </li>
            <li>
              Professional advisers including lawyers, bankers, auditors and
              insurers based in the UK.
            </li>
            <li>
              Regulators and other authorities who require reporting of
              processing activities in certain circumstances.
            </li>
            <li>
              Third parties to whom we may choose to sell, transfer or merge
              parts of our business or our assets. Alternatively, we may seek to
              acquire other businesses or merge with them. If a change happens
              to our business, then the new owners may use your personal data in
              the same way as set out in this privacy policy.
            </li>
            <li>Fraud prevention agencies.</li>
          </ul>
          <br />
          We require all third parties to respect the security of your personal
          data and to treat it in accordance with the law. We do not allow our
          third-party service providers to use your personal data for their own
          purposes and only permit them to process your personal data for
          specified purposes and in accordance with our instructions.
          <SpanWrapper>5. International transfers </SpanWrapper>
          Many of our external third parties are based outside the European
          Economic Area (“EEA”) so their processing of your personal data will
          involve a transfer of data outside the EEA.
          <br />
          <br />
          Whenever we transfer your personal data out of the EEA, we ensure a
          similar degree of protection is afforded to it by ensuring at least
          one of the following safeguards is implemented:
          <br />
          <br />
          <ul>
            <li>
              We will only transfer your personal data to countries that have
              been deemed to provide an adequate level of protection for
              personal data by the European Commission.
            </li>
            <li>
              Where we use certain service providers, we may use specific
              contracts approved by the European Commission which give personal
              data the same protection it has in Europe.
            </li>
            <li>
              Where we use providers based in the US, we may transfer data to
              them if they are part of the Privacy Shield which requires them to
              provide similar protection to personal data shared between Europe
              and the US.
            </li>
          </ul>
          <br />
          Please contact us if you want further information on the specific
          mechanism used by us when transferring your personal data out of the
          EEA.
          <SpanWrapper>6. Data security</SpanWrapper>
          We have put in place appropriate security measures to prevent your
          personal data from being accidentally lost, used or accessed in an
          unauthorised way, altered or disclosed. In addition, we limit access
          to your personal data to those employees, agents, contractors and
          other third parties who have a business need to know. They will only
          process your personal data on our instructions and they are subject to
          a duty of confidentiality.
          <br />
          <br />
          However, the transmission of information via the internet is never
          completely secure. Although we will do our best to protect your
          personal data, we cannot guarantee the security of your data
          transmitted to our site; any transmission is at your own risk.
          <br />
          <br />
          We have put in place procedures to deal with any suspected personal
          data breach and will notify you and any applicable regulator of a
          breach where we are legally required to do so.
          <SpanWrapper>7. Retention</SpanWrapper>
          We will only retain your personal information for as long as is
          necessary to carry out the purpose for which it was collected,
          including for the purposes of satisfying any legal, accounting, or
          reporting requirements.
          <br />
          <br />
          To determine the appropriate retention period for personal data, we
          consider the amount, nature, and sensitivity of the personal data, the
          potential risk of harm from unauthorised use or disclosure of your
          personal data, the purposes for which we process your personal data
          and whether we can achieve those purposes through other means, and the
          applicable legal requirements.
          <br />
          <br />
          In some circumstances we may anonymise your personal data (so that it
          can no longer be associated with you) for research or statistical
          purposes in which case we may use this information indefinitely
          without further notice to you.
          <SpanWrapper>8. Your rights</SpanWrapper>
          Under the Data Protection Legislation you may be entitled to the
          following rights:
          <br />
          <ul>
            <li>
              Where you have provided your consent to the processing of your
              personal data for any purpose, you have the right to withdraw such
              consent at any time by contacting us at Rock My Wedding, The Metro
              Building, 1 Butterwick,London, W6 8DL or by email to&nbsp;
              <a href="mailto:hello@rockmywedding.co.uk">
                hello@rockmywedding.co.uk
              </a>
              .
            </li>
            <li>
              You can ask us to rectify any inaccuracies in the personal
              information that we hold about you.
            </li>
            <li>
              You can request the erasure of personal information that we hold
              about you where there is no good reason for us to continue
              processing it, where you have successfully exercised your right to
              object to processing, where we may have processed your information
              unlawfully or where we are required to erase your personal data to
              comply with local law.
            </li>
            <li>
              You can object to our processing of your personal data where we
              are relying on a legitimate interest (or that of a third party)
              and there is something about your particular situation which makes
              you want to object to processing on this ground as you feel it
              impacts on your fundamental rights and freedoms. You also have the
              right to object where we are processing your personal data for
              direct marketing purposes. In some cases, we may demonstrate that
              we have compelling legitimate grounds to process your information
              which override your rights and freedoms.
            </li>
            <li>
              You can request that we restrict our processing of your personal
              data (a) if you want us to establish the data&apos;s accuracy; (b)
              where our use of the data is unlawful but you do not want us to
              erase it; (c) where you need us to hold the data even if we no
              longer require it as you need it to establish, exercise or defend
              legal claims; or (d) you have objected to our use of your data but
              we need to verify whether we have overriding legitimate grounds to
              use it.
            </li>
            <li>
              You can request a copy of your personal data from us in a commonly
              used and machine-readable format or that we transmit your personal
              data to another data controller.
            </li>
            <li>
              You can ask not to be subject to automated decision-making,
              including profiling, which has legal or other significant effects
              on you.
            </li>
            <li>
              You can request access to information held about you. You will not
              have to pay a fee to access your personal data (or to exercise any
              of the other rights). However, we may charge a reasonable fee if
              your request is clearly unfounded, repetitive or excessive.
              Alternatively, we may refuse to comply with your request in such
              circumstances.
            </li>
          </ul>
          <br />
          You can exercise any of the above rights at any time by contacting us
          at Rock My Wedding, The Metro Building, 1 Butterwick, London, W6 8DL
          or by email to&nbsp;
          <a href="mailto:hello@rockmywedding.co.uk">
            hello@rockmywedding.co.uk
          </a>
          .<SpanWrapper>9. Links to and from our site</SpanWrapper>
          Our site may, from time to time, contain links to and from the
          websites of our partners and affiliates. If you follow a link to any
          of these websites, please note that these websites have their own
          privacy policies and that we do not accept any responsibility or
          liability for these policies. Please check these policies before you
          submit any personal data to these websites.
          <SpanWrapper>10. Changes to your personal data</SpanWrapper>
          It is important that the personal data we hold about you is accurate
          and current. Please keep us informed if your personal data changes
          during your relationship with us.
          <SpanWrapper>11. Changes to our privacy policy</SpanWrapper>
          Any changes we make to our privacy policy in the future will be posted
          on this page. Please check back frequently to see any updates or
          changes to our privacy policy.
          <SpanWrapper>12. Contact</SpanWrapper>
          Questions, comments and requests regarding this privacy policy are
          welcomed and should be addressed to Rock My Wedding, The Metro
          Building, 1 Butterwick, London, W6 8DL or emailed to&nbsp;
          <a href="mailto:hello@rockmywedding.co.uk">
            hello@rockmywedding.co.uk
          </a>
          .
          <br />
          <br />
          If you have any complaints relating to our privacy policy or our use
          of your personal data, please contact us at Rock My Wedding, The Metro
          Building, 1 Butterwick, London, W6 8DL or by email to&nbsp;
          <a href="mailto:hello@rockmywedding.co.uk">
            hello@rockmywedding.co.uk
          </a>
          . You also have the right to make a complaint to the Information
          Commissioner’s Office (ICO), the UK supervisory authority for data
          protection issues. Further information can be found at www.ico.org.uk.
          <H4Wrapper>Cookie Notice</H4Wrapper>
          Our website uses cookies to help provide you with the best possible
          user experience. By continuing to use our site, you agree to our use
          of cookies. If you do not agree, you are able to adjust your cookies
          preferences for our website (and any other website) through your
          browser settings (more information on how to do this is given below).
          However, please bear in mind that by disabling cookies, the website
          may stop working properly for you, as we need to use cookies for the
          smooth running and functionality of our website.
          <br />
          <br />
          <ol type="1">
            <li>
              <strong>What are cookies?</strong>
              <br />
              Cookies are tiny text files, normally made up of letters and
              numbers. They are normally downloaded to your device when you
              browse the vast majority of websites to improve your experience of
              the site.
            </li>
            <li>
              <strong>Why do we use cookies?</strong>
              <br />
              We use cookies to help make our website work better, improve your
              online experience and help make sure that relevant content and
              functions are delivered and used more effectively. For example, we
              use cookies to help remember that you are logged in and to
              ‘remember’ how items you’ve added to your list or your shopping
              basket.
            </li>
            <li>
              <strong>How we use cookies?</strong>
              <br />
              The cookies we use are either ‘first party’ cookies (which are
              cookies that we place on your device) or ‘third-party’ cookies
              (which are cookies placed on your device by a party other than
              Rock My Wedding eg. trusted analytics partners that help us see
              how users move (anonymously, with no personal data collected)
              through our site so that we can learn how to make our couples’
              experience of our website even better or marketing partners who
              help us deliver tailored content to you.
              <br />
              <ul>
                <li>
                  All of these first-party and third-party cookies will fall
                  into four categories: Strictly Necessary cookies,
                  Functionality cookies, Performance cookies, and Marketing
                  cookies.
                </li>
                <li>
                  Strictly necessary cookies are cookies that are essential to
                  the operation of our website eg. logging in, adding gifts to
                  your list and your guests’ shopping baskets.
                </li>
                <li>
                  Functionality cookies are cookies that allow you to customise
                  how a website looks for you. Eg remembering that you’ve been
                  to our website before so that messages that are for first-time
                  users aren’t shown to you again.
                </li>
                <li>
                  Performance cookies collect statistical data on how users use
                  our website. This data doesn't include personal information
                  like names or addresses. These cookies are set by our
                  independent and trusted analytics partners to show us how
                  users move through our site, where they came from before
                  landing on our site e.g. from a wedding blog such as Rock My
                  Wedding, and what you click on in our website. We use this
                  data to continually optimise your online experience.
                </li>
                <li>
                  Marketing cookies help us to show you relevant and tailored
                  content. Third-party cookies that we use come from, for
                  example, Facebook, Bing, Pinterest, and Google Adsense.
                </li>
              </ul>
              <br />
              Any of the above cookies may be Persistent or Session cookies.
              Persistent cookies are those that remain on your device for a
              predetermined amount of time. Eg. We will remember that you were
              logged in recently so that when you start another session, you
              will automatically be logged in, meaning that you don’t need to
              provide your log-in details again. Session cookies will expire as
              soon as you end your session on your device.
            </li>
            <li>
              <strong>How you can control the use of cookies?</strong>
              <br />
              You can adjust your browser settings to manage cookies. How to do
              this will differ according to which web browser you use eg. Google
              Chrome, Safari, Microsoft Edge, or Mozilla Firefox. However,
              please bear in mind that by disabling cookies, the website may
              stop working properly for you, as we need to use cookies for the
              smooth running and functionality of our website.
            </li>
            <li>
              <strong>Contact Us</strong>
              <br />
              If you have any questions at all regarding this Cookie Notice,
              please let us know at&nbsp;
              <a href="mailto:hello@rockmywedding.co.uk">
                hello@rockmywedding.co.uk
              </a>
            </li>
          </ol>
        </ParagWrapper>
      </SectionContainer>
    </>
  )
}

export default observer(PrivacyPage)
